import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isInView } from '@theme/utilities';

/**
 * Store customization: minimal stand-in for motion's `inView` so this module doesn't pull the
 * 80 KB `motion` bundle from jsDelivr on every PDP. Calls `onEnter` when any target intersects;
 * its returned function (if any) runs when that target leaves.
 * @param {Element | Element[]} targets
 * @param {(element: Element, entry: IntersectionObserverEntry) => (void | (() => void))} onEnter
 * @param {{ amount?: 'some' | 'all' | number, margin?: string }} [options]
 */
function inView(targets, onEnter, { amount = 'some', margin = '0px' } = {}) {
  const threshold = amount === 'all' ? 1 : amount === 'some' ? 0 : amount;
  /** @type {WeakMap<Element, () => void>} */
  const leaveCallbacks = new WeakMap();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const onLeave = leaveCallbacks.get(entry.target);
        if (entry.isIntersecting) {
          if (onLeave) continue;
          const result = onEnter(entry.target, entry);
          if (typeof result === 'function') leaveCallbacks.set(entry.target, result);
        } else if (onLeave) {
          leaveCallbacks.delete(entry.target);
          onLeave();
        }
      }
    },
    { rootMargin: margin, threshold },
  );
  for (const el of Array.isArray(targets) ? targets : [targets]) observer.observe(el);
}

class StickyBuyButton extends HTMLElement {
  connectedCallback() {
    // Move to body to escape stacking context
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // connectedCallback will be called again after moving
    }

    this.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      const anchor = target?.closest('a[href^="#"]');

      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute('href');

        if (href) {
          document.querySelector(href)?.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }
    });

    // Listen for variant updates
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const targetId = this.dataset.targetId;

    if (targetId) {
      const targetElement = document.getElementById(targetId);
      const footerElements = [...document.querySelectorAll('.shopify-section-group-footer-group')];

      // Store customization: reveal only after the main button scrolls ABOVE the sticky-header
      // line (top margin = header height, 0 if not sticky); the tall bottom margin keeps it
      // hidden while the button is still below the fold, so it doesn't show on mobile PDP entry.
      const stickyHeader = document.querySelector('.shopify-section--header[sticky]');
      const headerHeight = stickyHeader
        ? parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0
        : 0;

      /** @type {Map<string, boolean>} */
      const elementsInView = new Map([
        ['target', targetElement ? targetElement.getBoundingClientRect().bottom > headerHeight : false],
      ]);
      footerElements.forEach((el, i) => elementsInView.set(`footer-${i}`, isInView(el)));

      const isVisible = () => ![...elementsInView.values()].some((value) => value);

      this.toggleAttribute('data-visible', isVisible());

      if (targetElement) {
        inView(
          targetElement,
          () => {
            elementsInView.set('target', true);
            this.toggleAttribute('data-visible', isVisible());

            return () => {
              elementsInView.set('target', false);
              this.toggleAttribute('data-visible', isVisible());
            };
          },
          { amount: 'some', margin: `-${headerHeight}px 0px 100000px 0px` },
        );
      }

      if (footerElements.length) {
        inView(
          footerElements,
          (e) => {
            const key = `footer-${footerElements.indexOf(e)}`;
            elementsInView.set(key, true);
            this.toggleAttribute('data-visible', isVisible());

            return () => {
              elementsInView.set(key, false);
              this.toggleAttribute('data-visible', isVisible());
            };
          },
          { amount: 'some' },
        );
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Handles variant update events to sync with new variant
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { html } = event.detail.data;

    // Match by target id so a foreign sticky button (another product context in the fetched page)
    // isn't morphed in. Fall back to the first one when no target id is set.
    const targetId = this.dataset.targetId;
    const newStickyButton =
      (targetId && html.querySelector(`sticky-buy-button[data-target-id="${CSS.escape(targetId)}"]`)) ||
      html.querySelector('sticky-buy-button');
    if (newStickyButton) {
      morph(this, newStickyButton);
    }
  };
}

// Store customization: guard the define. The Faslet app block's es-module-shims re-evaluates theme
// modules on the PDP and an unguarded second define throws NotSupportedError.
if (!customElements.get('sticky-buy-button')) {
  customElements.define('sticky-buy-button', StickyBuyButton);
}
